import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Zap, Target, Brain, Users, Shield, Globe, ArrowRight, Heart } from "lucide-react";
import { Button } from "../components/ui/button";

const About = () => {
  return (
    <div className="min-h-screen bg-obsidian">
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 bg-obsidian/80 backdrop-blur-md border-b border-[#222]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-crimson flex items-center justify-center">
              <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <span className="font-heading text-lg sm:text-2xl font-bold tracking-tight">REKRUTOR</span>
          </Link>
          <Link to="/">
            <Button className="bg-crimson hover:bg-crimson/90 text-white text-sm uppercase tracking-wider">
              Back to Home
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-28 sm:pt-36 pb-16 px-4 sm:px-6 text-center relative">
        <div className="absolute inset-0 bg-gradient-to-b from-crimson/10 to-transparent pointer-events-none" />
        <div className="max-w-4xl mx-auto relative z-10">
          <motion.h1
            className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold mb-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            ABOUT <span className="text-crimson">REKRUTOR</span>
          </motion.h1>
          <motion.p
            className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            We're building the hiring platform that mid-career professionals and forward-thinking recruiters actually deserve.
          </motion.p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4">
              OUR <span className="text-crimson">MISSION</span>
            </h2>
            <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-4">
              Traditional recruitment is broken. Job boards are noisy, hiring processes are opaque, and mid-career professionals pivoting into new industries are underserved.
            </p>
            <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
              Rekrutor connects talent with opportunity through AI-powered matching, transparent pricing, and tools that work for both sides of the hiring equation — whether you're a recruiter screening 200 applicants or a professional seeking your next career move.
            </p>
          </motion.div>
          <motion.div
            className="grid grid-cols-2 gap-4"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            {[
              { icon: Brain, label: "AI-Powered", desc: "Smart matching & screening" },
              { icon: Shield, label: "Transparent", desc: "No hidden fees, ever" },
              { icon: Globe, label: "Global Reach", desc: "Multi-currency, multi-language" },
              { icon: Heart, label: "Human-First", desc: "Technology serves people" },
            ].map((v, i) => (
              <div key={i} className="p-5 bg-obsidian-light border border-[#222] text-center">
                <v.icon className="w-6 h-6 text-crimson mx-auto mb-2" />
                <h4 className="text-sm font-bold text-white mb-1">{v.label}</h4>
                <p className="text-xs text-gray-500">{v.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-16 px-4 sm:px-6 bg-obsidian-light border-y border-[#222]">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-center mb-10">
            WHAT WE <span className="text-crimson">DO</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: "For Job Seekers",
                items: [
                  "AI-powered profile building from resume or LinkedIn",
                  "Smart job matching with percentage-based role-fit scores",
                  "AI interview practice with voice mode and video recording",
                  "Career insights, skill gap analysis, and roadmap PDFs",
                  "Resume optimization tailored to specific job postings",
                ],
              },
              {
                title: "For Recruiters",
                items: [
                  "AI job description generator — ATS-optimized in seconds",
                  "AI-powered candidate screening and ranking",
                  "CRM pipeline with Kanban board, notes, tags, reminders",
                  "Bulk messaging, applicant pipeline, and analytics dashboard",
                  "Video interview calls and calendar integration",
                ],
              },
              {
                title: "For Everyone",
                items: [
                  "Pay-as-you-go model — no forced subscriptions",
                  "Multi-currency support with live exchange rates",
                  "Community forums, events, and networking",
                  "Badge system, referral rewards, and gamification",
                  "Mobile-responsive with PWA support",
                ],
              },
            ].map((section, i) => (
              <motion.div
                key={i}
                className="p-6 bg-[#0a0a0a] border border-[#1a1a1a]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="font-heading text-lg font-bold text-crimson mb-4">{section.title}</h3>
                <ul className="space-y-2.5">
                  {section.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-300">
                      <ArrowRight className="w-3.5 h-3.5 text-crimson mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 text-center">
        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
          READY TO <span className="text-crimson">JOIN</span>?
        </h2>
        <p className="text-gray-400 mb-8">Start free. Upgrade when the need is real.</p>
        <Link to="/">
          <Button className="bg-crimson hover:bg-crimson/90 text-white font-bold uppercase tracking-wider px-10 py-5 text-lg">
            Get Started <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </Link>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#222]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-crimson flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading text-lg font-bold">REKRUTOR</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <Link to="/privacy" className="hover:text-crimson transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-crimson transition-colors">Terms</Link>
            <Link to="/support" className="hover:text-crimson transition-colors">Support</Link>
          </div>
          <p className="text-sm text-gray-500">&copy; 2025 Rekrutor. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default About;
